import type { FC, ReactNode } from 'react'

import { CircleCheck, TriangleAlert } from 'lucide-react'
import { formatNumber, formatPercent, formatRupiahCompact } from '@/lib/format'
import { TODAY, daysBetween, formatDateLong } from '@/lib/date'
import { SectionCard } from '@/components/dashboard/section-card'
import { ProgressBar } from '@/components/dashboard/progress-bar'
import { StatusBadge } from '@/components/dashboard/status-badge'
import { QUARTER_GOALS } from '@/data/metrics'

interface GoalsCardProps {
    className?: string
}

export const GoalsCard: FC<GoalsCardProps> = ({ className }): ReactNode => {
    const { mrr, newCustomers, churn } = QUARTER_GOALS
    const mrrProgress = (mrr.current / mrr.target) * 100
    const customerProgress = (newCustomers.current / newCustomers.target) * 100
    const churnUsage = (churn.current / churn.target) * 100
    const churnOnTrack = churn.current <= churn.target
    const daysLeft = daysBetween(TODAY, QUARTER_GOALS.endsAt)

    return (
        <SectionCard
            id='target'
            className={className}
            title={`Target ${QUARTER_GOALS.label}`}
            description={`Berakhir ${formatDateLong(QUARTER_GOALS.endsAt)}, ${daysLeft} hari lagi.`}
        >
            <ul className='space-y-6'>
                <li>
                    <div className='flex items-baseline justify-between gap-3'>
                        <p className='text-sm font-medium text-foreground'>
                            MRR akhir kuartal
                        </p>
                        <p className='text-sm font-semibold text-foreground tabular-nums'>
                            {formatPercent(mrrProgress, 0)}
                        </p>
                    </div>
                    <ProgressBar className='mt-2' value={mrrProgress} label='Progres target MRR' />
                    <p className='mt-1.5 text-xs text-muted-foreground tabular-nums'>
                        {formatRupiahCompact(mrr.current)} dari target {formatRupiahCompact(mrr.target)}
                    </p>
                </li>

                <li>
                    <div className='flex items-baseline justify-between gap-3'>
                        <p className='text-sm font-medium text-foreground'>
                            Pelanggan baru
                        </p>
                        <p className='text-sm font-semibold text-foreground tabular-nums'>
                            {formatPercent(customerProgress, 0)}
                        </p>
                    </div>
                    <ProgressBar className='mt-2' value={customerProgress} label='Progres target pelanggan baru' />
                    <p className='mt-1.5 text-xs text-muted-foreground tabular-nums'>
                        {formatNumber(newCustomers.current)} dari target {formatNumber(newCustomers.target)} pelanggan
                    </p>
                </li>

                <li>
                    <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-medium text-foreground'>
                            Churn bulanan di bawah {formatPercent(churn.target, 0)}
                        </p>
                        {churnOnTrack
                            ? <StatusBadge tone='success' icon={CircleCheck}>Aman</StatusBadge>
                            : <StatusBadge tone='danger' icon={TriangleAlert}>Melewati batas</StatusBadge>}
                    </div>
                    <ProgressBar className='mt-2' value={churnUsage} label='Churn dibanding batas target' color='var(--chart-5)' />
                    <p className='mt-1.5 text-xs text-muted-foreground tabular-nums'>
                        Saat ini {formatPercent(churn.current, 2)}, memakai {formatPercent(churnUsage, 0)} dari batas
                    </p>
                </li>
            </ul>
        </SectionCard>
    )
}
